/**
 * Resource-review API — thin wrapper over `/console/review/*`.
 *
 * The seven endpoints correspond one-to-one with the ones defined in
 * `docs/maker-checker-system/resource-review-api.md`. Every method
 * takes the resource name as its first argument and delegates the
 * heavy lifting (auth header, retries, base URL) to `apiClient`.
 *
 * Two design notes:
 *
 *   1. **Optional `AbortSignal`** — all list/read methods accept one
 *      so hooks that supersede requests on filter changes can cancel
 *      the previous one. This matches the pattern used by
 *      `trackingService` for the same reason.
 *
 *   2. **List envelope adapter** — the spec doesn't pin down whether
 *      the submissions list envelope is `{ data, total, page,
 *      page_size }` (like `/console/user_management/list`,
 *      `/console/invoice/pnc/list`, etc.) or the impact-assets shape
 *      `{ list, has_next_page, page_number, page_size }`. We adapt
 *      here so hooks see a stable shape either way — the moment we
 *      confirm one against staging we'll drop the fallback branch.
 */

import type { AxiosRequestConfig } from 'axios';
import { apiClient } from './apiClient';
import { apiConfig } from '@/config/apiConfig';
import type {
  ReviewableResource,
  ResourceMeta,
  SubmissionRow,
  SubmissionListParams,
  PaginatedSubmissions,
  UpdateSubmissionBody,
  UpdateSubmissionResponse,
  ChangeLogEntry,
  ChangeLogResponse,
  RestoreSubmissionBody,
  RestoreSubmissionResponse,
} from '@/features/impact-data/types';
import { toQueryParams } from '@/features/impact-data/utils/filterQuery';

const REVIEW_BASE = apiConfig.endpoints.review.base;

/** Build the per-resource sub-path (`/console/review/:resource/...`). */
function resourcePath(resource: string, suffix = ''): string {
  return `${REVIEW_BASE}/${encodeURIComponent(resource)}${suffix}`;
}

function submissionPath(resource: string, id: number | string, suffix = ''): string {
  return `${resourcePath(resource, '/submissions')}/${encodeURIComponent(String(id))}${suffix}`;
}

/**
 * Unwrap the outer `{ success, data: {...} }` envelope for the meta
 * endpoint. Anything that already looks like a `ResourceMeta` (has
 * `view_meta`) is returned untouched.
 */
function unwrapMeta(body: unknown): ResourceMeta {
  if (!body || typeof body !== 'object') return body as ResourceMeta;
  const b = body as Record<string, unknown>;
  if ('view_meta' in b) return b as unknown as ResourceMeta;
  if (b.data && typeof b.data === 'object' && 'view_meta' in (b.data as object)) {
    return b.data as ResourceMeta;
  }
  return b as unknown as ResourceMeta;
}

/**
 * Extract a single submission row out of every envelope shape we've
 * seen. The staging backend uses
 * `{ success: true, data: { record: {...row} } }`; we also accept
 * plain `{ data: {...row} }`, bare `{...row}`, and the
 * double-wrapped `{ success, data: { data: {...row} } }` variant.
 *
 * A row is recognised by having at least one of `id`, `updated_at`,
 * or a small handful of other row-identity fields (`Id` / `ID` for
 * legacy tables). That filter lets us peel the envelope without
 * mistaking it for a row.
 */
function extractRow(body: unknown): SubmissionRow | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return undefined;
  const b = body as Record<string, unknown>;
  if (looksLikeRow(b)) return b as SubmissionRow;

  if (b.data && typeof b.data === 'object' && !Array.isArray(b.data)) {
    const d = b.data as Record<string, unknown>;
    if (looksLikeRow(d)) return d as SubmissionRow;
    // Staging shape: `{ success, data: { record: {...row} } }`.
    if (d.record && typeof d.record === 'object' && !Array.isArray(d.record)) {
      const rec = d.record as Record<string, unknown>;
      if (looksLikeRow(rec)) return rec as SubmissionRow;
    }
    // Double-wrap: `{ success, data: { data: {...row} } }`.
    if (d.data && typeof d.data === 'object' && !Array.isArray(d.data)) {
      const dd = d.data as Record<string, unknown>;
      if (looksLikeRow(dd)) return dd as SubmissionRow;
    }
  }

  // Bare `{ record: {...row} }` without an outer envelope.
  if (b.record && typeof b.record === 'object' && !Array.isArray(b.record)) {
    const rec = b.record as Record<string, unknown>;
    if (looksLikeRow(rec)) return rec as SubmissionRow;
  }

  return undefined;
}

function looksLikeRow(obj: Record<string, unknown>): boolean {
  // Accept anything that has any of the row-identity fields — much
  // more permissive than requiring `id`, which some meta rows may
  // omit or send as `Id` / `ID` depending on the underlying table.
  if ('id' in obj || 'Id' in obj || 'ID' in obj) return true;
  if ('updated_at' in obj) return true;
  // A row won't have `success: boolean` as its identity field, but the
  // envelope will — so the presence of `success` disqualifies.
  if (typeof obj.success === 'boolean') return false;
  // Fallback: multiple non-envelope keys implies a row.
  const envelopeKeys = new Set(['success', 'data', 'error', 'code', 'message']);
  const nonEnvelope = Object.keys(obj).filter((k) => !envelopeKeys.has(k));
  return nonEnvelope.length >= 2;
}

/**
 * Extract the changelog envelope. Same shape-flexibility as
 * `extractResourceList` but produces a `ChangeLogResponse` so we
 * can propagate the `original_restore_available` hint from wherever
 * the server nests it (root of the outer envelope, or the same
 * `data` object that carries the list).
 */
function extractChangeLog(body: unknown): ChangeLogResponse {
  if (Array.isArray(body)) return { data: body as ChangeLogEntry[] };
  if (!body || typeof body !== 'object') return { data: [] };
  const b = body as Record<string, unknown>;

  // Prefer `data.list` (staging), then `data` array, then top-level
  // `list` / `changelog`.
  let entries: ChangeLogEntry[] | undefined;
  let restoreFlag: boolean | undefined;

  if (b.data && typeof b.data === 'object' && !Array.isArray(b.data)) {
    const inner = b.data as Record<string, unknown>;
    if (Array.isArray(inner.list)) entries = inner.list as ChangeLogEntry[];
    else if (Array.isArray(inner.data)) entries = inner.data as ChangeLogEntry[];
    else if (Array.isArray(inner.changelog)) entries = inner.changelog as ChangeLogEntry[];
    if (typeof inner.original_restore_available === 'boolean') {
      restoreFlag = inner.original_restore_available;
    }
  }
  if (!entries) {
    if (Array.isArray(b.list)) entries = b.list as ChangeLogEntry[];
    else if (Array.isArray(b.data)) entries = b.data as ChangeLogEntry[];
    else if (Array.isArray(b.changelog)) entries = b.changelog as ChangeLogEntry[];
  }
  if (restoreFlag === undefined && typeof b.original_restore_available === 'boolean') {
    restoreFlag = b.original_restore_available;
  }

  return {
    data: entries ?? [],
    ...(restoreFlag !== undefined ? { original_restore_available: restoreFlag } : {}),
  };
}

/**
 * Extract the resource array out of every envelope shape we've seen.
 * The staging backend uses `{ success, data: { list: [...] } }`;
 * older docs / other envs may use bare arrays or `{ data: [...] }`.
 * Anything unrecognised falls back to `[]` rather than throwing —
 * consumers already render a friendly empty state.
 */
function extractResourceList(body: unknown): ReviewableResource[] {
  if (Array.isArray(body)) return body as ReviewableResource[];
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  if (Array.isArray(b.list)) return b.list as ReviewableResource[];
  if (Array.isArray(b.resources)) return b.resources as ReviewableResource[];
  if (Array.isArray(b.data)) return b.data as ReviewableResource[];
  if (b.data && typeof b.data === 'object') {
    const d = b.data as Record<string, unknown>;
    if (Array.isArray(d.list)) return d.list as ReviewableResource[];
    if (Array.isArray(d.resources)) return d.resources as ReviewableResource[];
  }
  return [];
}

/**
 * Normalise every list-envelope shape we've seen into
 * `PaginatedSubmissions`. Handles:
 *
 *   - `[...]`                                                (bare array)
 *   - `{ data: [...] }`
 *   - `{ list: [...], total?, page_number?, page_size?, has_next_page? }`
 *   - `{ success, data: { list: [...], ...pagination } }`   (staging)
 *   - `{ data: [...], pagination: {...} }`                   (nested pagination)
 *   - `{ data: [...], pageInfo: {...} }`                     (camelCase variant)
 *
 * Pagination fields are looked up under a dozen common aliases —
 * whichever the server actually uses gets picked up. Unrecognised
 * shapes fall through to an empty page rather than crashing the UI.
 */
function normaliseListResponse(raw: unknown): PaginatedSubmissions {
  if (Array.isArray(raw)) return { data: raw as SubmissionRow[] };
  if (!raw || typeof raw !== 'object') return { data: [] };
  const r = raw as Record<string, unknown>;

  // Peel the outer envelope: `{ success, data: {...inner} }` where the
  // inner object holds either `list` or another `data` array.
  if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    const inner = r.data as Record<string, unknown>;
    if (Array.isArray(inner.list) || Array.isArray(inner.data)) {
      return normaliseListResponse(r.data);
    }
  }

  const dataArr = Array.isArray(r.list)
    ? (r.list as SubmissionRow[])
    : Array.isArray(r.data)
      ? (r.data as SubmissionRow[])
      : undefined;
  if (!dataArr) return { data: [] };

  // Pagination hints — look in top-level fields first, then in a
  // nested `pageInfo` / `pagination` object if the server nested them.
  const nested =
    (r.pageInfo && typeof r.pageInfo === 'object' ? (r.pageInfo as Record<string, unknown>) : undefined) ??
    (r.pagination && typeof r.pagination === 'object' ? (r.pagination as Record<string, unknown>) : undefined) ??
    {};

  const getNumber = (...keys: string[]): number | undefined => {
    for (const k of keys) {
      const v = r[k] ?? nested[k];
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    return undefined;
  };
  const getBoolean = (...keys: string[]): boolean | undefined => {
    for (const k of keys) {
      const v = r[k] ?? nested[k];
      if (typeof v === 'boolean') return v;
    }
    return undefined;
  };

  return {
    data: dataArr,
    total: getNumber('total', 'total_records', 'totalRecords', 'total_count', 'count'),
    page: getNumber('page', 'page_number', 'current_page', 'currentPage', 'pageNumber'),
    page_size: getNumber('page_size', 'pageSize', 'limit', 'per_page'),
    has_next_page: getBoolean('has_next_page', 'hasNextPage', 'hasNext', 'has_more'),
  };
}

export interface ListResourcesOptions {
  /**
   * Optional `program` query parameter. Accepts a single program
   * code (`RF`) or a CSV of codes (`SD,RGSH,RGSF,RGST,RGSG` for
   * Sports — matching the CSV convention used across the mis-api
   * for `/console/user_management/list?program=SD,TBTP`). Omitting
   * this yields the full role-filtered list — the previous default
   * behaviour for pages that don't scope by program.
   */
  program?: string;
}

class ReviewService {
  /**
   * List every resource the caller can review. The server filters
   * by JWT roles first, and by `?program=<code(s)>` when the caller
   * provides a program scope. Callers still get to slice further
   * client-side via `filterResourcesForProgram` as defense in depth,
   * so any resource the server accidentally returns cross-program
   * still gets pruned before it lands in the UI.
   *
   * The wire shape observed on staging is
   * `{ success: true, data: { list: [...] } }`; we also accept bare
   * arrays, `{ data: [...] }`, and `{ resources: [...] }` in case
   * other environments emit a different variant.
   */
  async getResources(
    options?: ListResourcesOptions,
    signal?: AbortSignal,
  ): Promise<ReviewableResource[]> {
    const params: Record<string, string> = {};
    if (options?.program && options.program.trim() !== '') {
      params.program = options.program;
    }
    const res = await apiClient.get<unknown>(apiConfig.endpoints.review.resources, {
      params: Object.keys(params).length > 0 ? params : undefined,
      signal,
    });
    return extractResourceList(res.data);
  }

  /**
   * Combined view-meta + edit-constraints for a resource. Cached long
   * on the server side — safe to call once per session per resource.
   *
   * The staging backend wraps everything under `{ success, data: {...} }`;
   * we unwrap defensively so consumers see the meta directly.
   */
  async getResourceMeta(resource: string, signal?: AbortSignal): Promise<ResourceMeta> {
    const res = await apiClient.get<unknown>(resourcePath(resource, '/meta'), { signal });
    return unwrapMeta(res.data);
  }

  /**
   * Paginated submissions list. `params` is passed straight through to
   * the query string via `toQueryParams` (drops empty values).
   */
  async listSubmissions(
    resource: string,
    params?: SubmissionListParams,
    signal?: AbortSignal,
  ): Promise<PaginatedSubmissions> {
    const config: AxiosRequestConfig = { params: toQueryParams(params), signal };
    const res = await apiClient.get<unknown>(resourcePath(resource, '/submissions'), config);
    return normaliseListResponse(res.data);
  }

  /**
   * Single submission — same shape as a list row. Use after PATCH /
   * RESTORE to refresh without reloading the whole page. Also used
   * by the edit dialog to pull a fresh full row when a caller passes
   * in a partial (summary-mode) row.
   *
   * Wire shapes we accept:
   *   - `{ success, data: {...row} }`  ← staging
   *   - `{ data: {...row} }`
   *   - `{...row}` (bare row)
   *
   * The row is recognised by having at least one of `id` / `title` /
   * `updated_at` — one of these is always present on any real row.
   * Empty / null responses map to `undefined` so callers can render
   * a friendly not-found state.
   */
  async getSubmission(
    resource: string,
    id: number | string,
    params?: SubmissionListParams,
    signal?: AbortSignal,
  ): Promise<SubmissionRow | undefined> {
    const config: AxiosRequestConfig = { params: toQueryParams(params), signal };
    const res = await apiClient.get<unknown>(submissionPath(resource, id), config);
    return extractRow(res.data);
  }

  /** Apply an edit. Server writes both the row and the audit entry in one transaction. */
  async updateSubmission(
    resource: string,
    id: number | string,
    body: UpdateSubmissionBody,
  ): Promise<UpdateSubmissionResponse> {
    const res = await apiClient.patch<UpdateSubmissionResponse>(submissionPath(resource, id), body);
    return res.data;
  }

  /**
   * Changelog for a row, newest first. When any real history exists,
   * the server appends a synthetic `original` entry (id: 'original',
   * operation: 'ORIGINAL'). See `types/changelog.ts` for the shape.
   *
   * Accepts every envelope shape we've seen: bare array,
   * `{ data: [...] }`, `{ changelog: [...] }`, and the staging
   * `{ success, data: { list: [...] } }`. The `original_restore_available`
   * flag is picked up from either the root or the nested `data`
   * object depending on where the server put it.
   */
  async getChangeLog(
    resource: string,
    id: number | string,
    signal?: AbortSignal,
  ): Promise<ChangeLogResponse> {
    const res = await apiClient.get<unknown>(submissionPath(resource, id, '/changelog'), { signal });
    return extractChangeLog(res.data);
  }

  /** Restore a row to a prior snapshot (specific version or `original`). */
  async restoreSubmission(
    resource: string,
    id: number | string,
    body: RestoreSubmissionBody,
  ): Promise<RestoreSubmissionResponse> {
    const res = await apiClient.post<RestoreSubmissionResponse>(submissionPath(resource, id, '/restore'), body);
    return res.data;
  }
}

export const reviewService = new ReviewService();
export type { ReviewService };
